import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, Settings } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useTenantFeaturePermissions } from "@/hooks/useTenantFeaturePermissions";
import { toast } from "sonner";

interface Tenant {
  id: string;
  name: string;
  slug: string;
  domain?: string;
  status: string;
  admin_user_id: string;
  created_at: string;
}

const AVAILABLE_FEATURES = [
  {
    key: 'custom_branding',
    name: 'Custom Branding',
    description: 'Vollständige White-Label Lösung mit eigenen Domains, Logos und Farben'
  },
  {
    key: 'advanced_analytics',
    name: 'Erweiterte Analytics',
    description: 'Zusätzliche Analyse-Features und Berichte'
  },
  {
    key: 'priority_support',
    name: 'Priority Support',
    description: 'Bevorzugter Support mit schnelleren Antwortzeiten'
  }
];

export function TenantFeatureManagement() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const { permissions, toggleFeaturePermission, hasFeaturePermission, loading: permissionsLoading } = useTenantFeaturePermissions();

  const fetchTenants = async () => {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTenants(data || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      toast.error('Fehler beim Laden der Tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const filteredTenants = tenants.filter(tenant =>
    tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (tenant.domain && tenant.domain.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleFeatureToggle = async (tenantId: string, featureKey: string, enabled: boolean) => {
    await toggleFeaturePermission(tenantId, featureKey, enabled);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 px-1 sm:px-0">
      <div className="space-y-2">
        <h2 className="text-xl sm:text-2xl font-bold">Feature Management</h2>
        <p className="text-sm sm:text-base text-muted-foreground">
          Verwalten Sie Feature-Berechtigungen für alle Tenants
        </p>
      </div>

      <div className="w-full">
        <div className="relative w-full">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Tenants durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 w-full"
          />
        </div>
      </div>

      <div className="grid gap-4 md:gap-6">
        {filteredTenants.map((tenant) => (
          <Card key={tenant.id}>
            <CardHeader className="pb-3 sm:pb-4">
              <div className="space-y-2">
                <CardTitle className="flex items-start sm:items-center gap-2 text-base sm:text-lg">
                  <Settings className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 mt-0.5 sm:mt-0" />
                  <span className="break-words">{tenant.name}</span>
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <span className="break-all">{tenant.slug}</span>
                    <span className="hidden sm:inline">•</span>
                    <span className="break-all">{tenant.domain || 'Keine Domain'}</span>
                    <Badge variant={tenant.status === 'active' ? 'default' : 'secondary'} className="self-start sm:ml-2 text-xs">
                      {tenant.status}
                    </Badge>
                  </div>
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-3 sm:space-y-4">
                <h4 className="font-medium text-sm sm:text-base">Feature-Berechtigungen</h4>
                <div className="grid gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {AVAILABLE_FEATURES.map((feature) => (
                    <div key={feature.key} className="space-y-2 p-3 border rounded-lg">
                      <div className="flex items-start space-x-2">
                        <Switch
                          id={`${tenant.id}-${feature.key}`}
                          checked={hasFeaturePermission(tenant.id, feature.key)}
                          onCheckedChange={(checked) => 
                            handleFeatureToggle(tenant.id, feature.key, checked)
                          }
                          disabled={permissionsLoading}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <Label
                            htmlFor={`${tenant.id}-${feature.key}`}
                            className="text-sm font-medium leading-tight block"
                          >
                            {feature.name}
                          </Label>
                          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredTenants.length === 0 && (
        <Card>
          <CardContent className="flex items-center justify-center p-8">
            <p className="text-muted-foreground">Keine Tenants gefunden</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}